/**
 * Comprehensive form validation utilities
 */

export const validators = {
  required: (message = 'This field is required') => (value) => {
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      return message;
    }
    return null;
  },

  email: (message = 'Please enter a valid email address') => (value) => {
    if (!value) return null; // Use required validator for empty checks
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      return message;
    }
    return null;
  },

  minLength: (min, message) => (value) => {
    if (!value) return null;
    if (value.length < min) {
      return message || `Must be at least ${min} characters`;
    }
    return null;
  },

  maxLength: (max, message) => (value) => {
    if (!value) return null;
    if (value.length > max) {
      return message || `Must be no more than ${max} characters`;
    }
    return null;
  },

  min: (min, message) => (value) => {
    if (!value && value !== 0) return null;
    const num = parseFloat(value);
    if (isNaN(num) || num < min) {
      return message || `Must be at least ${min}`;
    }
    return null;
  },

  max: (max, message) => (value) => {
    if (!value && value !== 0) return null;
    const num = parseFloat(value);
    if (isNaN(num) || num > max) {
      return message || `Must be no more than ${max}`;
    }
    return null;
  },

  url: (value, message = 'Please enter a valid URL') => {
    if (!value) return null;
    try {
      new URL(value);
      return null;
    } catch {
      return message;
    }
  },

  phone: (value, message = 'Please enter a valid phone number') => {
    if (!value) return null;
    const phoneRegex = /^[\d\s\-\+\(\)]+$/;
    if (!phoneRegex.test(value) || value.replace(/\D/g, '').length < 10) {
      return message;
    }
    return null;
  },

  date: (value, message = 'Please enter a valid date') => {
    if (!value) return null;
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return message;
    }
    return null;
  },

  futureDate: (value, message = 'Date must be in the future') => {
    if (!value) return null;
    const date = new Date(value);
    const now = new Date();
    if (date <= now) {
      return message;
    }
    return null;
  },

  pastDate: (value, message = 'Date must be in the past') => {
    if (!value) return null;
    const date = new Date(value);
    const now = new Date();
    if (date >= now) {
      return message;
    }
    return null;
  },

  pattern: (regex, message) => (value) => {
    if (!value) return null;
    if (!regex.test(value)) {
      return message || 'Invalid format';
    }
    return null;
  },

  match: (otherValue, message = 'Values do not match') => (value) => {
    if (value !== otherValue) {
      return message;
    }
    return null;
  }
};

/**
 * Validate a form field with multiple validators
 */
export const validateField = (value, fieldValidators) => {
  if (!Array.isArray(fieldValidators)) {
    fieldValidators = [fieldValidators];
  }

  for (const validator of fieldValidators) {
    const error = typeof validator === 'function'
      ? validator(value)
      : validator(value);

    if (error) {
      return error;
    }
  }

  return null;
};

/**
 * Validate an entire form
 */
export const validateForm = (values, schema) => {
  const errors = {};

  for (const [field, validators] of Object.entries(schema)) {
    const error = validateField(values[field], validators);
    if (error) {
      errors[field] = error;
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Debounced validation for real-time feedback
 */
export const createDebouncedValidator = (validator, delay = 300) => {
  let timeoutId;

  return (value, callback) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      const error = validator(value);
      callback(error);
    }, delay);
  };
};

/**
 * Validate booking form data
 */
export const validateBooking = (bookingData) => {
  const errors = {};

  if (!bookingData.name || !bookingData.name.trim()) {
    errors.name = 'Please enter your name';
  }

  if (!bookingData.email || !bookingData.email.trim()) {
    errors.email = 'Please enter your email';
  } else {
    const emailError = validators.email()(bookingData.email);
    if (emailError) {
      errors.email = emailError;
    }
  }

  if (!bookingData.date) {
    errors.date = 'Please select a date';
  } else {
    // Compare dates without time - allow today and future dates
    const selectedDate = new Date(bookingData.date);
    const today = new Date();
    // Reset both dates to start of day for comparison
    selectedDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    if (selectedDate < today) {
      errors.date = 'Please select a future date';
    }
  }

  if (!bookingData.time) {
    errors.time = 'Please select a time';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};
